<script>
import { authComputed } from '@store/helpers'
import PermissionHelper from '../utils/permissionHelper'

// Lucide icons ported as local SVGs (see ui-design/icons/*.vue).
// Individual, static imports, per Rule 1 of
// GREENN-ENG-DOC/front-end/design-system/icons.md
import LayoutDashboardIcon from './ui-design/icons/LayoutDashboardIcon.vue'
import UsersIcon from './ui-design/icons/UsersIcon.vue'
import ShoppingCartIcon from './ui-design/icons/ShoppingCartIcon.vue'
import TargetIcon from './ui-design/icons/TargetIcon.vue'
import FastForwardIcon from './ui-design/icons/FastForwardIcon.vue'
import RotateCcwIcon from './ui-design/icons/RotateCcwIcon.vue'
import CoinsIcon from './ui-design/icons/CoinsIcon.vue'
import BanknoteIcon from './ui-design/icons/BanknoteIcon.vue'
import LandmarkIcon from './ui-design/icons/LandmarkIcon.vue'
import PackageIcon from './ui-design/icons/PackageIcon.vue'
import CreditCardIcon from './ui-design/icons/CreditCardIcon.vue'
import FileSearchIcon from './ui-design/icons/FileSearchIcon.vue'
import MessageSquareWarningIcon from './ui-design/icons/MessageSquareWarningIcon.vue'
import ImageIcon from './ui-design/icons/ImageIcon.vue'
import TerminalIcon from './ui-design/icons/TerminalIcon.vue'
import ShoppingBagIcon from './ui-design/icons/ShoppingBagIcon.vue'
import ScaleIcon from './ui-design/icons/ScaleIcon.vue'
import RepeatIcon from './ui-design/icons/RepeatIcon.vue'
import ActivityIcon from './ui-design/icons/ActivityIcon.vue'
import ScanFaceIcon from './ui-design/icons/ScanFaceIcon.vue'
import SettingsIcon from './ui-design/icons/SettingsIcon.vue'
import BriefcaseBusinessIcon from './ui-design/icons/BriefcaseBusinessIcon.vue'
import WalletIcon from './ui-design/icons/WalletIcon.vue'
import ShieldAlertIcon from './ui-design/icons/ShieldAlertIcon.vue'
import ChevronDownIcon from './ui-design/icons/ChevronDownIcon.vue'

const STORAGE_PREFIX = 'erp:sidebar:v1:'

/**
 * Declarative menu definition.
 *
 * The permission condition lives HERE, as a field, instead of scattered
 * across the template as v-if. This is what allows CA-14 (a group with no
 * visible child renders neither header nor separator) without duplicating
 * the rule in two places: both the item and the group header go through the
 * same isVisible() function.
 *
 * `always: true`  -> item with no v-if in the original menu (Dashboard).
 * `permission`    -> key (or OR-array of keys) from PermissionHelper.getScreenPermissions().
 * `flag`          -> also requires being present in userFlags.
 *
 * Routes and permissions are exactly the same as the previous menu: nothing
 * here changes a route, a permission rule, or a flag value.
 */
const MENU = [
  {
    type: 'item',
    key: 'dashboard',
    label: 'Dashboard',
    to: '/dashboard',
    icon: 'LayoutDashboardIcon',
    always: true,
  },
  {
    type: 'group',
    key: 'comercial',
    label: 'Comercial',
    icon: 'BriefcaseBusinessIcon',
    collapsible: true,
    items: [
      { key: 'clientes', label: 'Cliente', to: '/clientes', icon: 'UsersIcon', permission: 'clientes' },
      { key: 'vendas', label: 'Vendas', to: '/vendas', icon: 'ShoppingCartIcon', permission: 'vendas' },
      { key: 'vendas-next', label: 'Vendas - Novo', to: '/vendas-next', icon: 'ShoppingBagIcon', permission: 'vendas', flag: 'ERP_SALES_MANAGEMENT' },
      { key: 'marcos', label: 'Marcos atingidos', to: '/marcos', icon: 'TargetIcon', permission: 'marcos' },
    ],
  },
  {
    type: 'group',
    key: 'financeiro',
    label: 'Financeiro',
    icon: 'WalletIcon',
    collapsible: true,
    items: [
      { key: 'antecipacao', label: 'Antecipação', to: '/antecipacao', icon: 'FastForwardIcon', permission: 'antecipacao' },
      { key: 'reembolsos', label: 'Reembolsos', to: '/reembolsos', icon: 'RotateCcwIcon', permission: 'reembolsos' },
      { key: 'cashback', label: 'Cashback', to: '/cashback', icon: 'CoinsIcon', permission: 'cashback' },
    ],
  },
  {
    type: 'group',
    key: 'risco',
    label: 'Risco e segurança',
    icon: 'ShieldAlertIcon',
    collapsible: true,
    items: [
      { key: 'saques', label: 'Saques', to: '/saques-agrupados', icon: 'BanknoteIcon', permission: 'saques' },
      { key: 'saques-x-grow', label: 'Saques X-GROW', to: '/saques-agrupados-x-grow', icon: 'LandmarkIcon', permission: 'xGrow', badge: 'BY GREENN' },
      { key: 'produtos', label: 'Produtos', to: '/produtos', icon: 'PackageIcon', permission: 'produtos' },
      { key: 'chargebacks', label: 'Chargeback', to: '/chargebacks', icon: 'CreditCardIcon', permission: 'chargebacks' },
      { key: 'revisao-documentos', label: 'Revisão de documentos', to: '/revisao-documentos', icon: 'FileSearchIcon', permission: 'revisaoDocumentos' },
      { key: 'analises', label: 'Análises de Risco', to: '/analises', icon: 'ActivityIcon', permission: 'analises' },
    ],
  },
  {
    type: 'group',
    key: 'atendimento',
    label: 'Atendimento',
    collapsible: false,
    items: [
      { key: 'reclamacao', label: 'Reclamações', to: '/reclamacao', icon: 'MessageSquareWarningIcon', permission: 'reclamacao' },
    ],
  },
  {
    type: 'group',
    key: 'marketing',
    label: 'Marketing',
    collapsible: false,
    items: [
      { key: 'banners', label: 'Banners', to: '/banners', icon: 'ImageIcon', permission: 'banners' },
    ],
  },
  {
    type: 'group',
    key: 'operacoes',
    label: 'Operações',
    collapsible: false,
    items: [
      // 'Operations' (item) kept in English by the user's decision, even
      // inside the 'Operações' (title) section, which is in Portuguese —
      // do not rename.
      { key: 'operations', label: 'Operations', to: '/operations', icon: 'TerminalIcon', permission: ['operations', 'operations_viewer'] },
      { key: 'conciliacao-vendas', label: 'Conciliação Vendas', to: '/conciliacao-vendas', icon: 'ScaleIcon', permission: 'conciliacaoVendas' },
      { key: 'conciliacao-assinatura', label: 'Conciliação Assinatura', to: '/conciliacao-assinatura', icon: 'RepeatIcon', permission: 'conciliacaoAssinatura' },
      { key: 'identidade', label: 'Validação de Identidade', to: '/identidade', icon: 'ScanFaceIcon', permission: 'identidade' },
      { key: 'configurations', label: 'Configuração', to: '/configurations', icon: 'SettingsIcon', permission: 'configurations' },
    ],
  },
]

export default {
  name: 'SideNav',

  components: {
    LayoutDashboardIcon,
    UsersIcon,
    ShoppingCartIcon,
    TargetIcon,
    FastForwardIcon,
    RotateCcwIcon,
    CoinsIcon,
    BanknoteIcon,
    LandmarkIcon,
    PackageIcon,
    CreditCardIcon,
    FileSearchIcon,
    MessageSquareWarningIcon,
    ImageIcon,
    TerminalIcon,
    ShoppingBagIcon,
    ScaleIcon,
    RepeatIcon,
    ActivityIcon,
    ScanFaceIcon,
    SettingsIcon,
    BriefcaseBusinessIcon,
    WalletIcon,
    ShieldAlertIcon,
    ChevronDownIcon,
  },

  data() {
    return {
      permissions: {},
      userFlags: [],
      openGroups: {},
      condensed: false,
      bodyObserver: null,
      scrollTimers: [],
    }
  },

  computed: {
    ...authComputed,

    /**
     * Menu already filtered by permission. Groups with no visible child are
     * dropped here (CA-14) — the header and the separator are rendered
     * inside the same block, so an orphan separator is never a possible state.
     */
    visibleMenu() {
      return MENU.map((node) => {
        if (node.type === 'item') {
          return this.isVisible(node) ? node : null
        }
        const items = node.items.filter((item) => this.isVisible(item))
        return items.length ? Object.assign({}, node, { items }) : null
      }).filter(Boolean)
    },

    /** Flattened list, in visual order — used by the compact rail. */
    flatVisibleItems() {
      const out = []
      this.visibleMenu.forEach((node) => {
        if (node.type === 'item') {
          out.push({ item: node, groupKey: null })
        } else {
          node.items.forEach((item) => out.push({ item, groupKey: node.key }))
        }
      })
      return out
    },

    storageKey() {
      const profile = this.$store && this.$store.getters ? this.$store.getters.getProfile : null
      const id = profile && profile.id
      // Without a resolved id there is no user-scoped key. In that case the
      // menu falls back to defaults and does NOT persist — we'd rather lose
      // persistence than leak state between accounts on the same browser (R7).
      //
      // No watcher on this computed: profile.id is synchronous in practice by
      // the time this component mounts. On reload it's hydrated from
      // localStorage before Vue even boots (store/modules/user/index.js). On
      // the post-login redirect, `userSuccess` commits the profile before
      // `redirectUserToHome` dispatches the navigation that mounts this
      // component (store/modules/user/actions.js) — `userRequest` is only
      // ever dispatched from the login actions, never after mount. No
      // reachable path leaves storageKey resolving after mounted() runs.
      return id ? STORAGE_PREFIX + id : null
    },
  },

  watch: {
    // CA-10: the group that contains the current route must be expanded. Since
    // navigation is now SPA, this has to react to every route change — it
    // can't depend on mounted(), which only worked before because of a full
    // page reload.
    $route() {
      this.expandActiveGroup()
      // The group only opens on the next tick; the active item doesn't have
      // its final position before that.
      this.scheduleScrollActive()
    },

    // Switching expanded <-> compact swaps the rendered tree (v-if/v-else),
    // so the active item becomes a different element, inside a different container.
    condensed() {
      this.scheduleScrollActive()
    },
  },

  async created() {
    try {
      const permissionHelper = new PermissionHelper(this.$store)
      this.permissions = permissionHelper.getScreenPermissions() || {}
    } catch (error) {
      console.error('Erro ao carregar permissões do menu:', error)
      this.permissions = {}
    }

    try {
      let flags = this.$store.getters.getUserFlags

      if (!flags || flags.length === 0) {
        await this.$store.dispatch('fetchUserFlags')
        flags = this.$store.getters.getUserFlags
      }

      this.userFlags = Array.isArray(flags) ? flags : []
    } catch (error) {
      console.error('Erro ao buscar flags do usuário:', error)
      this.userFlags = []
    }
  },

  mounted() {
    this.condensed = document.body.classList.contains('enlarged')
    this.restoreState()
    this.expandActiveGroup()
    this.watchBodyClass()
    this.scheduleScrollActive()
  },

  beforeDestroy() {
    this.scrollTimers.forEach(clearTimeout)
    this.scrollTimers = []

    if (this.bodyObserver) {
      this.bodyObserver.disconnect()
      this.bodyObserver = null
    }
  },

  methods: {
    /**
     * The only place in the component that knows how to read a permission.
     * Both item and group header go through here — there is no second copy
     * of the rule.
     */
    isVisible(item) {
      if (item.always) return true

      const keys = Array.isArray(item.permission) ? item.permission : [item.permission]
      if (!keys.some((key) => this.permissions[key])) return false

      if (item.flag && !this.userFlags.includes(item.flag)) return false
      return true
    },

    isActive(to) {
      const current = this.$route ? this.$route.path : ''
      return current === to || current.indexOf(to + '/') === 0
    },

    isGroupOpen(group) {
      if (!group.collapsible) return true
      return !!this.openGroups[group.key]
    },

    isGroupActive(group) {
      return (group.items || []).some((item) => this.isActive(item.to))
    },

    toggleGroup(group) {
      if (!group.collapsible) return // CA-06: a simple section is not an accordion
      // CA-04: multi-open accordion — opening one doesn't close the others.
      this.$set(this.openGroups, group.key, !this.openGroups[group.key])
      this.persistState()
    },

    /**
     * CA-10: opens the group that contains the current route.
     *
     * Persists the same way a manual toggle does — in-memory state and
     * localStorage must not diverge just because a group opened
     * automatically instead of by click.
     */
    expandActiveGroup() {
      let changed = false
      MENU.forEach((node) => {
        if (node.type !== 'group' || !node.collapsible) return
        const hit = node.items.some((item) => this.isActive(item.to))
        if (hit && !this.openGroups[node.key]) {
          this.$set(this.openGroups, node.key, true)
          changed = true
        }
      })
      if (changed) this.persistState()
    },

    /**
     * Walks up from the item to find whoever actually scrolls, NEVER going
     * past `.left-side-menu`. The page is deliberately out of reach: CA-10
     * forbids scrolling the page because of this adjustment.
     */
    findScroller(el) {
      const limite = this.$el.closest('.left-side-menu')
      let node = el.parentElement

      while (node) {
        if (node.scrollHeight > node.clientHeight + 1) return node
        if (node === limite || node === document.body) return null
        node = node.parentElement
      }

      return null
    },

    /**
     * The menu's final height only exists after three things that DON'T
     * happen in the same tick: `created()` is async (waits on
     * `fetchUserFlags`, which changes `visibleMenu`), `restoreState()`
     * reopens the saved groups (their sublists only render on the next
     * tick), and PerfectScrollbar measures the container on mount. Measuring
     * too early gives the old position and the active item doesn't scroll.
     *
     * That's why this runs a few times within a short window instead of just
     * once. It's safe to repeat: `scrollActiveIntoView` does nothing once the
     * item already fits entirely, so the extra passes become no-ops as soon
     * as the layout settles.
     *
     * `setTimeout`, not `requestAnimationFrame`, is deliberate: in the
     * portal's WebView rAF freezes (`visibilityState: hidden`) and the passes
     * would never run.
     */
    scheduleScrollActive() {
      this.scrollTimers.forEach(clearTimeout)
      this.scrollTimers = [0, 120, 400].map((ms) =>
        setTimeout(() => this.scrollActiveIntoView(), ms)
      )
      this.$nextTick(this.scrollActiveIntoView)
    },

    /**
     * CA-10: brings the active item into the internal scroller's visible area.
     *
     * Moves the MINIMUM necessary — doesn't center and doesn't align to the
     * top, which the criterion deliberately left open. If the item is
     * already fully visible, nothing happens: free scrolling on every
     * navigation is worse than not scrolling at all.
     *
     * The adjustment is instant, with no `behavior: 'smooth'`: smooth
     * scrolling depends on rAF, and in the portal's WebView (visibilityState
     * hidden) rAF freezes — the animation would stay stuck halfway forever.
     */
    scrollActiveIntoView() {
      const el = this.$el.querySelector(
        '.side-nav__link.is-active, .side-nav__rail-link.is-active'
      )
      if (!el) return

      const scroller = this.findScroller(el)
      if (!scroller) return

      const er = el.getBoundingClientRect()
      const sr = scroller.getBoundingClientRect()
      const topo = er.top - sr.top + scroller.scrollTop
      const base = topo + er.height
      const folga = 8

      if (topo < scroller.scrollTop) {
        scroller.scrollTop = Math.max(0, topo - folga)
      } else if (base > scroller.scrollTop + scroller.clientHeight) {
        scroller.scrollTop = base - scroller.clientHeight + folga
      }
    },

    // ---- persistence (CA-05 / CA-09), scoped per user (R7) ----

    restoreState() {
      const key = this.storageKey
      if (!key) return
      try {
        const raw = window.localStorage.getItem(key)
        if (!raw) return
        const saved = JSON.parse(raw)
        if (saved && typeof saved.groups === 'object' && saved.groups) {
          Object.keys(saved.groups).forEach((k) => {
            this.$set(this.openGroups, k, !!saved.groups[k])
          })
        }
      } catch (error) {
        // localStorage throws in private mode / when cookies are blocked. A
        // throw here would take down the entire navigation, so we just fall
        // back to the default.
        console.warn('Não foi possível restaurar o estado da sidebar:', error)
      }
    },

    persistState() {
      const key = this.storageKey
      if (!key) return
      try {
        const raw = window.localStorage.getItem(key)
        const saved = raw ? JSON.parse(raw) : {}
        saved.groups = Object.assign({}, this.openGroups)
        window.localStorage.setItem(key, JSON.stringify(saved))
      } catch (error) {
        console.warn('Não foi possível salvar o estado da sidebar:', error)
      }
    },

    /**
     * The expanded/compact state lives in body.enlarged, toggled by the
     * layout (router/layouts/main.vue). We observe the class instead of
     * receiving a prop, to avoid widening the blast radius up to side-bar.vue.
     */
    watchBodyClass() {
      if (typeof MutationObserver === 'undefined') return
      this.bodyObserver = new MutationObserver(() => {
        const next = document.body.classList.contains('enlarged')
        if (next !== this.condensed) this.condensed = next
      })
      this.bodyObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ['class'],
      })
    },
  },
}
</script>

<template>
  <!--- Sidemenu -->
  <div id="sidebar-menu">
    <!-- ============ EXPANDED MODE ============ -->
    <!-- No "Navegação" (Navigation) title: it doesn't exist in the design
         (nodes 6514:2702 and 6508:2212 start directly with Dashboard) and
         CA-01 doesn't mention it. -->
    <ul v-if="!condensed" id="side-menu" class="side-nav">
      <template v-for="node in visibleMenu">
        <!-- standalone item (Dashboard) -->
        <li v-if="node.type === 'item'" :key="node.key" class="side-nav__item">
          <router-link
            :to="node.to"
            class="side-nav__link"
            :class="{ 'is-active': isActive(node.to) }"
          >
            <component :is="node.icon" :size="18" class="side-nav__icon" />
            <span class="side-nav__label">{{ node.label }}</span>
          </router-link>
        </li>

        <!-- group: collapsible (with chevron) or simple section (no chevron) -->
        <li v-else :key="node.key" class="side-nav__group">
          <button
            v-if="node.collapsible"
            type="button"
            class="side-nav__group-head has-dropdown"
            :class="{ 'is-open': isGroupOpen(node), 'is-active': isGroupActive(node) }"
            :aria-expanded="isGroupOpen(node) ? 'true' : 'false'"
            @click="toggleGroup(node)"
          >
            <component :is="node.icon" :size="18" class="side-nav__icon" />
            <span class="side-nav__label">{{ node.label }}</span>
            <ChevronDownIcon
              :size="16"
              class="side-nav__chevron"
              :class="{ 'is-open': isGroupOpen(node) }"
            />
          </button>

          <!-- Simple section (Atendimento/Marketing): no icon, no pill,
               per artboard 01/02-menu-erp. -->
          <div v-else class="side-nav__section-head">
            <span class="side-nav__label">{{ node.label }}</span>
          </div>

          <ul v-show="isGroupOpen(node)" class="side-nav__sublist">
            <li v-for="item in node.items" :key="item.key" class="side-nav__item">
              <router-link
                :to="item.to"
                class="side-nav__link side-nav__link--child"
                :class="{ 'is-active': isActive(item.to) }"
              >
                <component :is="item.icon" :size="18" class="side-nav__icon" />
                <span class="side-nav__label">{{ item.label }}</span>
                <small v-if="item.badge" class="side-nav__badge">{{ item.badge }}</small>
              </router-link>
            </li>
          </ul>
        </li>
      </template>
    </ul>

    <!-- ============ COMPACT MODE: flattened rail (artboard 03-menu-erp) ============ -->
    <ul v-else id="side-menu" class="side-nav side-nav--rail">
      <template v-for="(entry, index) in flatVisibleItems">
        <li
          v-if="index > 0 && entry.groupKey !== flatVisibleItems[index - 1].groupKey"
          :key="entry.item.key + '-sep'"
          class="side-nav__rail-sep"
          role="separator"
        ></li>
        <li :key="entry.item.key" class="side-nav__item">
          <router-link
            v-b-tooltip.hover.right
            :to="entry.item.to"
            :title="entry.item.label"
            class="side-nav__rail-link"
            :class="{ 'is-active': isActive(entry.item.to) }"
          >
            <component :is="entry.item.icon" :size="16" />
            <span class="sr-only">{{ entry.item.label }}</span>
          </router-link>
        </li>
      </template>
    </ul>

    <div class="clearfix"></div>
  </div>
  <!-- End Sidebar -->
</template>

<style lang="scss">
// CA-16: Montserrat scoped to the menu. The font is already served by the
// <link> from Google Fonts in public/index.html — no new package. We don't
// touch $font-family-base, which would apply to the whole ERP (out of scope).
$sidenav-font: 'Montserrat', 'Nunito', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

// Tokens from Figma ("Melhorias ERP | Greenn", node 6483:573), read from the
// file's variables — not estimated.
$sidenav-accent: #22c55e;        // accent/green
$sidenav-accent-soft: #e7f5ea;   // accent/greenLight
$sidenav-ink: #000c0b;           // Gray/500
$sidenav-line: #e5e7e7;          // Gray/50
$sidenav-radius: 12px;           // radii/component
$sidenav-pill-inset: 12px;       // pill's lateral margin (doesn't touch the edge)
$sidenav-row-height: 46px;       // Figma: EVERY instance is 247x46 (node 6508:2212)

// Shared box geometry for every top-level sidebar row (link, group header,
// section header) — Figma (6508:2212): all three measure 247x46 with the
// same lateral margin/padding. One mixin so the three selectors can't drift.
@mixin sidenav-row-box {
  width: calc(100% - #{$sidenav-pill-inset * 2});
  margin: 2px $sidenav-pill-inset;
  padding: 9px 12px;
}

#sidebar-menu {
  font-family: $sidenav-font;

  .side-nav {
    list-style: none;
    margin: 0;
    padding: 8px 0 24px;
  }

  .side-nav__sublist {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .side-nav__link,
  .side-nav__group-head,
  .side-nav__section-head {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    // Figma (6508:2212): EVERY instance measures 247x46 — item, group
    // header, and simple section. Padding alone gave a 38px box.
    min-height: $sidenav-row-height;
    padding: 9px 20px;
    font-family: $sidenav-font;
    font-size: 0.86rem;
    font-weight: 500;
    color: #6c757d;
    background: none;
    border: 0;
    text-align: left;
    text-decoration: none;
    transition: background-color 0.15s ease, color 0.15s ease;
  }

  .side-nav__link:hover,
  .side-nav__group-head:hover {
    color: $sidenav-accent;
    background-color: rgba(34, 197, 94, 0.08);
  }

  // TL's decision (Figma has no visible active item in the expanded state):
  // the active item is a PILL, same margin/radius rule as the group header —
  // an edge-to-edge stripe would be the only element out of step with the set.
  // The geometry goes on .side-nav__link (all of them), not just .is-active:
  // if only the active one got the margin, the text would jump 12px on navigation.
  .side-nav__link {
    @include sidenav-row-box;
    border-radius: $sidenav-radius;
  }

  .side-nav__link.is-active {
    color: $sidenav-accent;
    background-color: $sidenav-accent-soft;
    font-weight: 600;
  }

  // CA-02 / Figma: the group header is a rounded PILL with lateral margin —
  // not an edge-to-edge stripe. But it's FULL WIDTH within that margin, it
  // doesn't shrink to content: in node 6508:2212 the five headers
  // (6508:2216/2222/2232/2240/2243) measure 247x46, exactly like the items.
  // The previous `width: auto` gave 149px because <button> shrinks to
  // content (fit-content) even with display:flex — <a> doesn't. The calc
  // makes both equal.
  .side-nav__group-head {
    @include sidenav-row-box;
    border-radius: $sidenav-radius;
  }

  .side-nav__group-head.is-active {
    color: $sidenav-accent;
    background-color: $sidenav-accent-soft;
    font-weight: 600;
  }

  // Simple section (Atendimento/Marketing): plain box, bold, dark color,
  // no icon and no pill.
  // "Atendimento"/"Marketing" are also among the 247x46 instances in Figma:
  // same box and same inset as the group header, without pill or icon.
  // The `padding-top: 14px` I'd added for breathing room was removed — it
  // inflated the box to 51px. If breathing room is missed, it's a single
  // `margin-top` line.
  .side-nav__section-head {
    @include sidenav-row-box;
    color: $sidenav-ink;
    font-size: 0.86rem;
    font-weight: 700;
    letter-spacing: 0;
    text-transform: none;
    cursor: default;
  }

  // 12px of pill margin + 34 = 46px absolute, the same indentation as before.
  .side-nav__link--child {
    padding-left: 34px;
  }

  .side-nav__icon {
    flex: none;
  }

  .side-nav__label {
    flex: 1 1 auto;
    min-width: 0;
  }

  .side-nav__chevron {
    flex: none;
    transition: transform 0.18s ease;
  }

  .side-nav__chevron.is-open {
    transform: rotate(180deg);
  }

  .side-nav__badge {
    flex: none;
    font-size: 0.6rem;
    letter-spacing: 0.04em;
    opacity: 0.75;
  }

  // ---------- compact mode ----------
  // Figma measurements, whole-app artboard (node 6508:1838), rail column =
  // frame 6597:1013:
  //   column 81px | item 49x46 | vertical step 50px (46 + 4 gap)
  //   lateral padding 16px on each side (81 - 49 = 32) | icon 16x16
  //   separator 33x1, 8px clear above and below | next group 63px top-to-top
  // The ul becomes a flex column with an explicit gap: in block layout the
  // vertical margins would collapse between the <li>s and the step would be
  // 48 instead of 50.
  .side-nav--rail {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    margin: 0;
    padding: 10px 0 24px;
    list-style: none;
  }

  .side-nav__rail-link {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 49px;
    height: 46px;
    margin: 0;
    // Figma: the rail artboard's variables (6508:2120) return
    // radii/component = 12 — the same token as the expanded pill.
    border-radius: $sidenav-radius;
    color: #6c757d;
    text-decoration: none;
    transition: background-color 0.15s ease, color 0.15s ease;
  }

  // The rail is a flex container: without flex-none the SVG gets squeezed by
  // the layout down to a dot. We also fix width/height so the theme
  // (body.enlarged, _left-menu.scss) can't resize the icon from outside.
  .side-nav__rail-link > svg {
    flex: none;
    width: 16px;
    height: 16px;
  }

  .side-nav__rail-link:hover {
    color: $sidenav-accent;
    background-color: rgba(34, 197, 94, 0.1);
  }

  // CA-10 in compact mode: rounded light-green background on the current route's item.
  .side-nav__rail-link.is-active {
    color: $sidenav-accent;
    background-color: $sidenav-accent-soft;
  }

  // Separator 33x1, 8px clear above and below (Figma 6597:1013, all 4 dividers).
  // The flex gap already covers 4 of those 8, hence margin 4. Figma's 63px is
  // the top-to-top pitch — 46 (item) + 8 + 1 + 8 — not the gap between items.
  .side-nav__rail-sep {
    flex: none;
    width: 33px;
    height: 1px;
    margin: 4px 0;
    background-color: $sidenav-line;
    list-style: none;
  }

  // The theme (Coderthemes) does a hover-expand on body.enlarged, revealing
  // the labels. That conflicts with CA-08's tooltip, so we neutralize the
  // behavior within the menu's scope.
  .side-nav--rail .side-nav__label {
    display: none;
  }

  :focus-visible {
    outline: 2px solid $sidenav-accent;
    outline-offset: -2px;
    border-radius: 6px;
  }
}

// The theme (Coderthemes) doesn't shrink the column on body.enlarged: the
// sidebar stayed at 240px and the content started at 240, leaving an empty
// white strip next to the rail. We adjust the column width and the content
// margin here, within the menu's scope — without touching _left-menu.scss,
// which is global and serves the whole app.
// Measured in Figma: frame 6597:1013, x=1, width=81, inside the 1440
// artboard (6508:1838). It's the real column, not artboard padding.
// The mockup leaves the content at x=240 (the same white strip we fix
// here) — a mockup artifact, not reproduced: the content hugs the rail.
$rail-width: 81px;
// Topbar height, measured in the browser (column top = 70px in both modes).
$topbar-height: 70px;

// The theme declares the collapsed column in 4 places, ALL with !important
// (_left-menu.scss: .enlarged .left-side-menu, .logo-box, .content-page,
// .footer — all at $leftbar-width-collapsed = 70px). Without !important here
// our rules lose and the column goes back to 70: that's what measuring in
// the browser showed. We repeat !important instead of editing
// _left-menu.scss, which is global and serves the whole app.
body.enlarged {
  // OPTION B (CA-10 in compact mode). In expanded mode the theme closes the
  // column's height with `position: fixed; top: 70px; bottom: 0` — 768 - 70
  // = 698px — which is why VuePerfectScrollbar (`h-100`) has something to
  // measure against. In compact mode the theme switches to
  // `position: absolute`, the containing block becomes the #wrapper
  // stretched by the theme's `body.enlarged { min-height: 1200px }`, and
  // `top: 70 + bottom: 0` resolves to 1200 - 70 = **1130px**: the column
  // grows with the content and nothing scrolls.
  //
  // Here we don't depend on inherited height: we give it a real height.
  // Measured in the browser: column top = 70px in both modes (topbar height).
  // `.left-side-menu` directly contains `#sidebar-menu`, so it's the list's
  // own scroller — and `findScroller` finds it by walking up through overflow.
  //
  // `overflow-x: hidden` isn't decorative: the theme's hover-expand widens
  // the item to `calc(190px + 70px)` = 260px inside an 81px column, which
  // would trigger a horizontal bar (that was the `ps--active-x` that showed
  // up when we tested option A).
  .left-side-menu {
    width: $rail-width !important;
    min-width: $rail-width;
    height: calc(100vh - #{$topbar-height});
    overflow-x: hidden;
    overflow-y: auto;
  }

  // The logo lives in the topbar, outside the menu, but aligns with the
  // column: without this it stayed at 70 next to an 81px rail.
  .logo-box {
    width: $rail-width !important;
  }

  .content-page {
    margin-left: $rail-width !important;
  }

  .footer {
    left: $rail-width !important;
  }

  #sidebar-menu .side-nav--rail {
    width: $rail-width;
  }

  // The theme has `.enlarged #sidebar-menu > ul > li > a { padding: 15px
  // 20px; min-height: 54px }` — specificity (1,1,3), higher than our block
  // `#sidebar-menu .side-nav__rail-link` (1,1,0). Measured result: a 49x54
  // item instead of 49x46. This selector gives (1,3,1) and wins without
  // !important.
  #sidebar-menu .side-nav--rail .side-nav__rail-link {
    height: 46px;
    min-height: 0;
    padding: 0;
  }
}
</style>
